/**
 * Tiny localStorage-backed draft persister for Add Tenant.
 *
 * Keyed by concessionaireId because the Add flow has no tenant id yet.
 * Saves the entire form state object verbatim plus a timestamp; the
 * caller decides whether to restore it (banner asks the user).
 *
 * The `tenant_logo` field — a File object from <input type="file"> — is
 * not serialisable, so it's stripped before saving and the user has to
 * re-pick the file after a refresh. Every other field round-trips.
 */

const DRAFT_KEY_PREFIX = 'tenant-draft-';

function key(concessionaireId) {
    return `${DRAFT_KEY_PREFIX}${concessionaireId || 'unknown'}`;
}

export function saveDraft(concessionaireId, form) {
    try {
        const { tenant_logo, ...serialisable } = form || {};
        const payload = { form: serialisable, savedAt: Date.now() };
        localStorage.setItem(key(concessionaireId), JSON.stringify(payload));
    } catch (e) {
        // Quota exceeded or storage disabled — drop silently. The draft is
        // a nice-to-have; never block submission.
        console.warn('saveDraft failed:', e);
    }
}

export function loadDraft(concessionaireId) {
    try {
        const raw = localStorage.getItem(key(concessionaireId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || !parsed.form) return null;
        return parsed; // { form, savedAt }
    } catch {
        return null;
    }
}

export function clearDraft(concessionaireId) {
    try { localStorage.removeItem(key(concessionaireId)); } catch { /* ignore */ }
}

/** Short relative-time string for the draft banner. Pass the caller's
 *  i18next `t` to localise; falls back to plain English interpolation. */
export function relativeTime(ms, t) {
    const tr = t || ((key, opts) => key.replace('{{count}}', opts.count));
    const secs = Math.max(1, Math.round((Date.now() - ms) / 1000));
    if (secs < 60) return tr('{{count}}s ago', { count: secs });
    const mins = Math.round(secs / 60);
    if (mins < 60) return tr('{{count}} min ago', { count: mins });
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return tr('{{count}} hr ago', { count: hrs });
    const days = Math.round(hrs / 24);
    return days === 1 ? tr('{{count}} day ago', { count: days }) : tr('{{count}} days ago', { count: days });
}
